import React from 'react';
import { FiFileText, FiZap } from 'react-icons/fi';
import { Button } from '../../ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import IconWrapper from '../../ui/IconWrapper';
import type { FOSDecisionDetail } from '../../../hooks/useFOSAdmin';
import {
  formatDate,
  getDecisionOutcomeColor,
  getProcessingStatusInfo,
} from './fosAdminDashboardUtils';

interface FOSDecisionDetailDialogProps {
  selectedDecisionId: string | null;
  decisionDetail?: FOSDecisionDetail;
  detailLoading: boolean;
  reprocessSinglePending: boolean;
  onOpenChange: (open: boolean) => void;
  onProcessSingle: (decisionId: string) => void;
}

const FOSDecisionDetailDialog: React.FC<FOSDecisionDetailDialogProps> = ({
  selectedDecisionId,
  decisionDetail,
  detailLoading,
  reprocessSinglePending,
  onOpenChange,
  onProcessSingle,
}) => (
  <Dialog open={!!selectedDecisionId} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <IconWrapper icon={FiFileText} className="h-5 w-5" />
          Decision Details
        </DialogTitle>
      </DialogHeader>

      {detailLoading ? (
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mr-3" />
          Loading...
        </div>
      ) : decisionDetail ? (
        <div className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-500">Reference</p>
              <p className="font-medium">{decisionDetail.reference}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Business</p>
              <p className="font-medium">{decisionDetail.business_name || '-'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Decision Date</p>
              <p className="font-medium">{formatDate(decisionDetail.decision_date)}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Industry</p>
              <p className="font-medium">{decisionDetail.industry_sector}</p>
            </div>
          </div>

          <div className="flex flex-wrap gap-3">
            <div>
              <p className="text-sm text-gray-500 mb-1">Outcome</p>
              <span className={`px-3 py-1 text-sm font-medium rounded-full ${getDecisionOutcomeColor(decisionDetail.decision_status)}`}>
                {decisionDetail.decision_status}
              </span>
            </div>
            <div>
              <p className="text-sm text-gray-500 mb-1">Processing Status</p>
              <span className={`px-3 py-1 text-sm font-medium rounded-full ${getProcessingStatusInfo(decisionDetail.processing_status).color}`}>
                {getProcessingStatusInfo(decisionDetail.processing_status).label}
              </span>
            </div>
            <div>
              <p className="text-sm text-gray-500 mb-1">Search Ready</p>
              <span className={`px-3 py-1 text-sm font-medium rounded-full ${decisionDetail.has_embedding ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                {decisionDetail.has_embedding ? 'Yes' : 'No'}
              </span>
            </div>
          </div>

          {getProcessingStatusInfo(decisionDetail.processing_status).canProcess && (
            <div className="p-4 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-800 mb-2">
                This decision needs processing to generate a summary and enable search.
              </p>
              <Button
                onClick={() => onProcessSingle(decisionDetail.id)}
                disabled={reprocessSinglePending}
              >
                <IconWrapper icon={FiZap} className={`h-4 w-4 mr-2 ${reprocessSinglePending ? 'animate-spin' : ''}`} />
                {reprocessSinglePending ? 'Processing...' : 'Process This Decision'}
              </Button>
            </div>
          )}

          {decisionDetail.summary && (
            <div>
              <p className="text-sm text-gray-500 mb-2">AI Summary</p>
              <p className="text-sm bg-gray-50 p-4 rounded-lg">{decisionDetail.summary}</p>
            </div>
          )}

          {decisionDetail.structured_summary && (
            <div>
              <p className="text-sm text-gray-500 mb-2">Structured Details</p>
              <pre className="text-xs bg-gray-50 p-4 rounded-lg overflow-x-auto">
                {JSON.stringify(decisionDetail.structured_summary, null, 2)}
              </pre>
            </div>
          )}

          <div className="border-t pt-4 text-xs text-gray-400">
            <p>Full text: {decisionDetail.full_text_length?.toLocaleString() || 0} characters</p>
            <p>Updated: {formatDate(decisionDetail.updated_at)}</p>
          </div>
        </div>
      ) : (
        <div className="text-center py-8 text-gray-500">Decision not found</div>
      )}
    </DialogContent>
  </Dialog>
);

export default FOSDecisionDetailDialog;
